/**
 * Ticket Lifecycle v2 — Slice 5D-12 Batch 2 + Slice 5D-14 Batch 2.5.
 *
 * Board-level advisor bay-hold data provider. Fetches custody-event
 * data for ALL visible tickets in a SINGLE query so individual
 * `TicketCard` instances never run their own custody / advisor
 * queries (Batch 1 left those as `null` to avoid N+1 — Batch 2
 * filled OBSERVED; Batch 2.5 adds PREDICTED via a bounded fan-out
 * of `useAdvisorBucketMetrics`-equivalent queries keyed on UNIQUE
 * advisor IDs, never per ticket).
 *
 * Scope (Batch 2.5):
 *   - OBSERVED advisor bay hold continues to flow as in Batch 2.
 *   - PREDICTED advisor bay hold now flows when:
 *       * `advisorTimingAnalyticsEnabled === true` (caller passes the
 *         resolved flag — same gate the single-ticket lifecycle card
 *         uses),
 *       * the ticket has NO usable observed value, and
 *       * the ticket has a current FOH custody holder, and
 *       * that advisor's bucket metric for the ticket's job-mix
 *         bucket has finite mean minutes AND confidence ∈
 *         {'low','medium','high'}.
 *     Otherwise `predictedAdvisorDelayMinutes` stays `null` and
 *     `source` stays `'unavailable'`.
 *
 * Scheduling-inert by construction:
 *   The server-side `bayOccupancyScheduling` helper short-circuits
 *   to `not_observed` whenever `source !== 'observed'`. Predicted
 *   minutes shown on the Dayboard NEVER influence `tickets.opti_time`
 *   or `tickets.opti_finish` via the existing Batch 3 path. A future
 *   batch may relax that gate behind a separate addon.
 *
 * Boundary:
 *   - This hook is allowed to import read-only lifecycle helpers,
 *     `useAdvisorBucketMetrics`'s extracted `fetchAdvisorBucketMetrics`,
 *     and supabase. It MUST NOT import Chronomancer / globalCascade /
 *     reassignCore / scheduling-write helpers / technician-efficiency
 *     math. Drift detector boundary guard pins this.
 *
 * Performance:
 *   - 1 batched query for custody events (Batch 2; observed).
 *   - 1 batched query for ticket_custody rows (Batch 2.5; current FOH
 *     holders), only when `advisorTimingAnalyticsEnabled === true`.
 *   - N React-Query queries (one per UNIQUE current FOH advisor —
 *     typically 1-3 advisors on a board) via `useQueries`, sharing
 *     the cache key shape `['advisor-bucket-metrics', ...]` with
 *     `useAdvisorBucketMetrics` so the existing per-ticket lifecycle
 *     card hits the same cache.
 *   - Zero queries when `enabled === false`.
 */

import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  deriveTimeBuckets,
  type CustodyEventLite,
} from '@/lib/tickets/lifecycle/deriveTimeBuckets';
import type { CustodyEventKind } from '@/lib/tickets/lifecycle/types';
import {
  fetchAdvisorBucketMetrics,
  getAdvisorBucketMetricsWindowStartIso,
} from '@/hooks/tickets/useAdvisorBucketMetrics';
import {
  classifyTicketJobMix,
  type JobMixJobLite,
  type TicketJobMixBucket,
} from '@/lib/tickets/lifecycle/jobMixClassifier';
import type {
  AdvisorBucketConfidence,
  AdvisorBucketMetricsResult,
} from '@/lib/tickets/lifecycle/advisorBucketMetrics';

/**
 * The advisor bay-hold data attached to a single ticket. Pass-through
 * shape consumed by `TicketCard` and folded into Batch 1's
 * `calculateAdvisorBayHoldMinutes` helper.
 *
 *   observedAdvisorDelayMinutes : raw advisor-held bay minutes from
 *                                 `deriveTimeBuckets`. `null` when no
 *                                 custody events exist for the ticket
 *                                 OR the events produced 0 minutes
 *                                 (treated as "not enough data yet"
 *                                 rather than a noisy "+0m observed").
 *   predictedAdvisorDelayMinutes: Batch 2.5 — bucket-mean advisor delay
 *                                 for the current FOH holder, only when
 *                                 observed is unavailable AND the
 *                                 bucket's confidence is non-insufficient.
 *   predictedConfidence         : the categorical confidence of the
 *                                 above prediction; `null` when no
 *                                 prediction was computed.
 *   source                      : 'observed' when observed minutes are
 *                                 finite > 0; 'predicted' when observed
 *                                 is unavailable but predicted is
 *                                 usable; 'unavailable' otherwise.
 */
export interface TicketAdvisorBayHoldData {
  observedAdvisorDelayMinutes: number | null;
  predictedAdvisorDelayMinutes: number | null;
  predictedConfidence: AdvisorBucketConfidence | null;
  source: 'observed' | 'predicted' | 'unavailable';
}

/**
 * Minimal ticket shape the provider needs. `created_at` is required
 * by `deriveTimeBuckets`. Batch 2.5 also accepts the optional
 * `is_comeback` flag + `flat_jobs` array used by
 * `classifyTicketJobMix`; when absent, predicted is unavailable for
 * that ticket but observed continues to work.
 */
export interface BoardTicketLite {
  id: string;
  created_at?: string | null;
  terminal_at?: string | null;
  /** Used by the predicted path via `classifyTicketJobMix`. */
  is_comeback?: boolean | null;
  /**
   * Pre-flattened job category/submenu/etc. the classifier needs.
   * Built at the Dashboard level from the already-loaded ticket data
   * so this hook does NOT need to run its own ticket_jobs query.
   * Absent → predicted unavailable for that ticket.
   */
  flat_jobs?: ReadonlyArray<JobMixJobLite> | null;
}

const TIMING_EVENT_KINDS: CustodyEventKind[] = [
  'kick_to_foh',
  'foh_accept',
  'kick_to_boh',
  'boh_accept',
  'reject_back',
];

// ===================================================================
// Pure compute step — extended for predicted (Batch 2.5)
// ===================================================================

/**
 * Slice 5D-14 Batch 2.5: per-ticket predicted input passed alongside
 * the custody events. Built by the React hook from the per-advisor
 * bucket metrics queries + classified job-mix bucket; exposed here
 * so the pure mapping step can be unit-tested without React or
 * supabase.
 */
export interface PredictedAdvisorBayHoldInput {
  minutes: number | null;
  confidence: AdvisorBucketConfidence | null;
}

/**
 * Returns the predicted minutes value if and only if the confidence
 * is usable (`'low' | 'medium' | 'high'`) AND minutes are finite ≥ 0.
 * `'insufficient'` confidence yields `null` so the UI doesn't show a
 * tentative prediction with an admission it shouldn't be trusted.
 */
function selectUsablePredictedMinutes(
  prediction: PredictedAdvisorBayHoldInput | null | undefined,
): { minutes: number; confidence: AdvisorBucketConfidence } | null {
  if (!prediction) return null;
  if (prediction.confidence === 'insufficient' || prediction.confidence === null) return null;
  if (typeof prediction.minutes !== 'number' || !Number.isFinite(prediction.minutes)) return null;
  const clamped = prediction.minutes < 0 ? 0 : prediction.minutes;
  return { minutes: clamped, confidence: prediction.confidence };
}

/**
 * Pure compute step. Same inputs ⇒ same output; no `Date.now()` calls
 * (caller passes `referenceTime`).
 *
 * Resolution precedence per ticket:
 *   1. Observed > 0       → source='observed', predicted/confidence cleared.
 *   2. Observed null/0
 *      AND usable predicted (low/medium/high + finite minutes)
 *                         → source='predicted', minutes from prediction.
 *   3. Otherwise          → source='unavailable'.
 */
export function buildAdvisorBayHoldMap(params: {
  tickets: ReadonlyArray<BoardTicketLite>;
  custodyEvents: ReadonlyArray<CustodyEventLite>;
  referenceTime: string;
  /**
   * Batch 2.5: optional per-ticket predicted input. When absent or
   * keys are missing, predicted path is skipped — semantics are
   * identical to Batch 2.
   */
  predictedByTicketId?: ReadonlyMap<string, PredictedAdvisorBayHoldInput | null>;
}): Map<string, TicketAdvisorBayHoldData> {
  const { tickets, custodyEvents, referenceTime, predictedByTicketId } = params;
  const eventsByTicket = new Map<string, CustodyEventLite[]>();
  for (const ev of custodyEvents) {
    const list = eventsByTicket.get(ev.ticket_id) ?? [];
    list.push(ev);
    eventsByTicket.set(ev.ticket_id, list);
  }

  const out = new Map<string, TicketAdvisorBayHoldData>();
  for (const ticket of tickets) {
    const events = eventsByTicket.get(ticket.id) ?? [];
    let observed: number | null = null;
    if (events.length > 0) {
      const buckets = deriveTimeBuckets({
        optiTime: null,
        createdAt: ticket.created_at ?? null,
        terminalAt: ticket.terminal_at ?? null,
        referenceTime,
        custodyEvents: events,
      });
      const candidate = buckets.advisorObservedMinutes;
      if (Number.isFinite(candidate) && candidate > 0) {
        observed = candidate;
      }
    }

    if (observed !== null) {
      // Observed strictly wins; predicted is cleared so a future
      // consumer can't ambiguate which source was applied.
      out.set(ticket.id, {
        observedAdvisorDelayMinutes: observed,
        predictedAdvisorDelayMinutes: null,
        predictedConfidence: null,
        source: 'observed',
      });
      continue;
    }

    const usable = selectUsablePredictedMinutes(
      predictedByTicketId?.get(ticket.id) ?? null,
    );
    if (usable !== null) {
      out.set(ticket.id, {
        observedAdvisorDelayMinutes: null,
        predictedAdvisorDelayMinutes: usable.minutes,
        predictedConfidence: usable.confidence,
        source: 'predicted',
      });
      continue;
    }

    out.set(ticket.id, {
      observedAdvisorDelayMinutes: null,
      predictedAdvisorDelayMinutes: null,
      predictedConfidence: null,
      source: 'unavailable',
    });
  }
  return out;
}

// ===================================================================
// Hook entrypoint
// ===================================================================

export interface UseBoardAdvisorBayHoldArgs {
  shopId: string | null | undefined;
  tickets: ReadonlyArray<BoardTicketLite>;
  enabled: boolean;
  /**
   * Slice 5D-14 Batch 2.5: gates the predicted-path queries. When
   * false (or absent), only OBSERVED is computed — same as Batch 2.
   * When true, the hook ALSO runs a batched ticket_custody query
   * and a bounded `useQueries` fan-out per UNIQUE current FOH
   * advisor (typically 1-3 advisors on a board).
   */
  advisorTimingAnalyticsEnabled?: boolean;
}

/** Internal: shape of `ticket_custody` row used to identify FOH holder. */
interface CurrentCustodyRow {
  ticket_id: string;
  custody_owner: string | null;
  holder_user_id: string | null;
}

export function useBoardAdvisorBayHold({
  shopId,
  tickets,
  enabled,
  advisorTimingAnalyticsEnabled = false,
}: UseBoardAdvisorBayHoldArgs): Map<string, TicketAdvisorBayHoldData> | null {
  const sortedIds = useMemo(() => {
    if (!enabled || !shopId || tickets.length === 0) return [] as string[];
    return tickets
      .map((t) => t.id)
      .filter((id) => typeof id === 'string' && id.length > 0)
      .sort();
  }, [enabled, shopId, tickets]);

  // Snapshot the wall clock per-render once per visible-id-set so the
  // query functions stay pure.
  const referenceTime = useMemo(
    () => new Date().toISOString(),
    [sortedIds.length > 0 ? sortedIds.join(',') : ''],
  );

  const queryEnabled = enabled && !!shopId && sortedIds.length > 0;
  const predictedEnabled = queryEnabled && advisorTimingAnalyticsEnabled === true;

  // -----------------------------------------------------------------
  // (1) Batched custody events — observed source.
  // -----------------------------------------------------------------
  const custodyEventsQuery = useQuery({
    queryKey: ['board-advisor-bay-hold-events', shopId, sortedIds],
    enabled: queryEnabled,
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<ReadonlyArray<CustodyEventLite>> => {
      const { data: rows, error } = await (supabase as any)
        .from('ticket_custody_events')
        .select('event_kind, ticket_id, created_at')
        .eq('shop_id', shopId)
        .in('ticket_id', sortedIds)
        .in('event_kind', TIMING_EVENT_KINDS);
      if (error) {
        console.error('[board-advisor-bay-hold] custody events query failed', error);
        throw error;
      }
      return ((rows ?? []) as Array<{
        event_kind: string;
        ticket_id: string;
        created_at: string;
      }>).map<CustodyEventLite>((r) => ({
        event_kind: r.event_kind as CustodyEventKind,
        ticket_id: r.ticket_id,
        created_at: r.created_at,
      }));
    },
  });

  // -----------------------------------------------------------------
  // (2) Batched current-FOH-holder rows — predicted source gating.
  //     Only fires when the predicted path is enabled.
  // -----------------------------------------------------------------
  const currentCustodyQuery = useQuery({
    queryKey: ['board-advisor-bay-hold-current-custody', shopId, sortedIds],
    enabled: predictedEnabled,
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<ReadonlyArray<CurrentCustodyRow>> => {
      const { data: rows, error } = await (supabase as any)
        .from('ticket_custody')
        .select('ticket_id, custody_owner, holder_user_id')
        .in('ticket_id', sortedIds)
        .eq('custody_owner', 'FOH');
      if (error) {
        console.error('[board-advisor-bay-hold] current custody query failed', error);
        throw error;
      }
      return (rows ?? []) as CurrentCustodyRow[];
    },
  });

  // -----------------------------------------------------------------
  // Derive the per-ticket prediction inputs (job-mix bucket + current
  // FOH holder id) so we know which unique advisor metrics to fetch.
  // -----------------------------------------------------------------
  const observedByTicketId = useMemo(() => {
    const map = new Map<string, number>();
    const events = custodyEventsQuery.data ?? [];
    if (events.length === 0) return map;
    // Group events per ticket and run deriveTimeBuckets once each so
    // we can decide which tickets need predicted lookup.
    const byTicket = new Map<string, CustodyEventLite[]>();
    for (const ev of events) {
      const list = byTicket.get(ev.ticket_id) ?? [];
      list.push(ev);
      byTicket.set(ev.ticket_id, list);
    }
    for (const ticket of tickets) {
      const list = byTicket.get(ticket.id);
      if (!list || list.length === 0) continue;
      const b = deriveTimeBuckets({
        optiTime: null,
        createdAt: ticket.created_at ?? null,
        terminalAt: ticket.terminal_at ?? null,
        referenceTime,
        custodyEvents: list,
      });
      if (Number.isFinite(b.advisorObservedMinutes) && b.advisorObservedMinutes > 0) {
        map.set(ticket.id, b.advisorObservedMinutes);
      }
    }
    return map;
  }, [custodyEventsQuery.data, tickets, referenceTime]);

  const currentFohHolderByTicketId = useMemo(() => {
    const map = new Map<string, string>();
    const rows = currentCustodyQuery.data ?? [];
    for (const r of rows) {
      if (r.custody_owner === 'FOH' && r.holder_user_id) {
        map.set(r.ticket_id, r.holder_user_id);
      }
    }
    return map;
  }, [currentCustodyQuery.data]);

  /**
   * Tickets that NEED a predicted lookup:
   *   - predicted path enabled
   *   - no usable observed value
   *   - have a current FOH holder
   *   - have flat_jobs to classify
   */
  const ticketsNeedingPredicted = useMemo(() => {
    if (!predictedEnabled) return [] as Array<{ ticketId: string; advisorId: string; bucket: TicketJobMixBucket }>;
    const out: Array<{ ticketId: string; advisorId: string; bucket: TicketJobMixBucket }> = [];
    for (const t of tickets) {
      if (observedByTicketId.has(t.id)) continue;
      const advisorId = currentFohHolderByTicketId.get(t.id);
      if (!advisorId) continue;
      const flat = t.flat_jobs ?? null;
      if (!flat) continue;
      const classification = classifyTicketJobMix(
        { is_comeback: t.is_comeback ?? false },
        flat,
      );
      out.push({ ticketId: t.id, advisorId, bucket: classification.bucket });
    }
    return out;
  }, [predictedEnabled, tickets, observedByTicketId, currentFohHolderByTicketId]);

  const uniqueAdvisorIds = useMemo(() => {
    const set = new Set<string>();
    for (const t of ticketsNeedingPredicted) set.add(t.advisorId);
    return Array.from(set).sort();
  }, [ticketsNeedingPredicted]);

  // -----------------------------------------------------------------
  // (3) Per-unique-advisor bucket-metrics fan-out via useQueries.
  //     Shares the cache key shape with `useAdvisorBucketMetrics`.
  // -----------------------------------------------------------------
  const advisorMetricsWindowStart = useMemo(getAdvisorBucketMetricsWindowStartIso, []);

  const advisorMetricsQueries = useQueries({
    queries: uniqueAdvisorIds.map((advisorId) => ({
      queryKey: ['advisor-bucket-metrics', shopId, advisorId, advisorMetricsWindowStart],
      enabled: predictedEnabled,
      retry: false,
      // 5-minute freshness — bucket metrics are 30-day windows; one
      // refetch per board mount is plenty.
      staleTime: 5 * 60_000,
      queryFn: () =>
        fetchAdvisorBucketMetrics({
          shopId: shopId as string,
          advisorUserId: advisorId,
          windowStartIso: advisorMetricsWindowStart,
        }),
    })),
  });

  const advisorMetricsByAdvisor = useMemo(() => {
    const map = new Map<string, AdvisorBucketMetricsResult | null>();
    advisorMetricsQueries.forEach((q, i) => {
      map.set(uniqueAdvisorIds[i], (q.data as AdvisorBucketMetricsResult | undefined) ?? null);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisorMetricsQueries.map((q) => q.data).join('|'), uniqueAdvisorIds]);

  const predictedByTicketId = useMemo(() => {
    const map = new Map<string, PredictedAdvisorBayHoldInput | null>();
    if (!predictedEnabled) return map;
    for (const { ticketId, advisorId, bucket } of ticketsNeedingPredicted) {
      const metrics = advisorMetricsByAdvisor.get(advisorId);
      if (!metrics) continue;
      const bucketMetric = metrics[bucket];
      if (!bucketMetric) continue;
      map.set(ticketId, {
        minutes: bucketMetric.averageAdvisorDelayImpactMinutes,
        confidence: bucketMetric.confidence,
      });
    }
    return map;
  }, [predictedEnabled, ticketsNeedingPredicted, advisorMetricsByAdvisor]);

  // -----------------------------------------------------------------
  // Final composition. When the observed query is still loading we
  // return null so the caller's TicketCard fallback shows the lower
  // bound — same flag-OFF parity Batch 2 had.
  // -----------------------------------------------------------------
  return useMemo(() => {
    if (!queryEnabled) return null;
    if (!custodyEventsQuery.data) return null;
    return buildAdvisorBayHoldMap({
      tickets,
      custodyEvents: custodyEventsQuery.data,
      referenceTime,
      predictedByTicketId,
    });
  }, [queryEnabled, custodyEventsQuery.data, tickets, referenceTime, predictedByTicketId]);
}
